import { ImportResult, ImportSection } from '../../types';

function Counts({ label, s }: { label: string; s: ImportSection }) {
  return (
    <div className="rounded-md border border-gray-200 dark:border-gray-700 p-3">
      <p className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</p>
      <p className="mt-1 text-sm">
        <span className="font-semibold text-green-700 dark:text-green-400">{s.created}</span> new ·{' '}
        <span className="font-semibold text-brand-700 dark:text-brand-300">{s.updated}</span> updated ·{' '}
        <span className="font-semibold text-gray-600 dark:text-gray-300">{s.skipped}</span> unchanged
      </p>
    </div>
  );
}

function Items({ s }: { s: ImportSection }) {
  const changing = s.items.filter(i => i.action !== 'skip' || i.reason !== 'unchanged');
  if (changing.length === 0) return null;
  return (
    <ul className="text-sm space-y-0.5">
      {changing.map(i => (
        <li key={`${i.action}-${i.name}`} className="flex gap-2">
          <span className={`shrink-0 w-16 text-xs uppercase tracking-wider ${
            i.action === 'create' ? 'text-green-700 dark:text-green-400'
              : i.action === 'update' ? 'text-brand-700 dark:text-brand-300'
                : 'text-gray-500'
          }`}>{i.action}</span>
          <span className="min-w-0 break-words">
            {i.name}
            {i.reason && i.reason !== 'unchanged' && <span className="text-gray-500 dark:text-gray-400"> — {i.reason}</span>}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * What an import will do, from the server's dry run — the same code path the
 * real import takes, rolled back. Shown before anything is written.
 */
export function ImportPreview({ result }: { result: ImportResult }) {
  const creds = [
    ...result.credentials_required.services.map(c => ({ kind: 'Service', ...c })),
    ...result.credentials_required.notifications.map(c => ({ kind: 'Channel', ...c })),
  ];
  const nothing = ['groups', 'services', 'notifications'].every(k => {
    const s = result[k as 'groups' | 'services' | 'notifications'];
    return s.created === 0 && s.updated === 0;
  });

  return (
    <div className="space-y-4" data-testid="import-preview">
      <div className="grid gap-3 sm:grid-cols-3">
        <Counts label="Groups" s={result.groups} />
        <Counts label="Services" s={result.services} />
        <Counts label="Channels" s={result.notifications} />
      </div>

      {nothing ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Everything in this file already matches. Applying it would change nothing.</p>
      ) : (
        <div className="space-y-3">
          <Items s={result.groups} />
          <Items s={result.services} />
          <Items s={result.notifications} />
        </div>
      )}

      {creds.length > 0 && (
        <div className="rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-3">
          <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
            {creds.length} {creds.length === 1 ? 'item needs' : 'items need'} credentials re-entered after import
          </p>
          <ul className="mt-1 text-sm text-amber-900/90 dark:text-amber-200/90 space-y-0.5">
            {creds.map((c, i) => (
              <li key={i}><span className="text-xs uppercase tracking-wider mr-1">{c.kind}</span>{c.name} — {c.reason}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
